import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Endpoint, HelpIndex, UsersAndGroupsMembershipActionType } from '../../../models/enums';
import { GenericColDef, GridContext, CellTemplateType, GridRowSelectionType } from '../../../models/grid-context';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { LeftRightSelectionBoxComponent } from '../../left-right-selection-box/left-right-selection-box.component';
import * as _ from 'lodash';
import { Role } from '../../../models/role';
import { createOrgNameOrIdRoleHandles, isNullOrUndefined } from '../../../models/utils';
import { RowNode } from 'ag-grid-community';

@Component({
    standalone: false,
    selector: 'res-users-and-groups-membership-modal',
    templateUrl: './users-and-groups-membership-modal.component.html',
    styleUrls: ['./users-and-groups-membership-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UsersAndGroupsMembershipModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    @ViewChild(LeftRightSelectionBoxComponent, { static: true }) leftRightSelectionBoxComponent: LeftRightSelectionBoxComponent;

    modalAutomationId = 'add-to-remove-from-organizations-modal';
    modalClass = 'system-users-and-groups-membership-modal';
    modalHelpIndex = HelpIndex.org_member_select_popup_dlg;
    leftListTitle = 'Available Organizations';
    rightListTitle = 'Assigned Organizations';
    filterPlaceholder = 'Filter by Name ...';
    itemsLeft = new Array<any>();
    itemsRight = new Array<any>();
    modalTitle = 'Add to / Remove from Organizations';

    nameColumn = 'Organization Name';
    roleColumnName = 'User Role';
    isBusy = false;
    isBusyMessage = 'Assigning ...';
    isSaveDisabled: boolean = false;

    roleList: Array<Role>;
    selectedRole: Role;

    filterLeftQuery = '';
    filterRightQuery = '';
    filteredLeftList = [];
    filteredRightList = [];
    debounceFilterLeft = _.debounce(this.filterAfterBounceLeft, 1000);
    debounceFilterRight = _.debounce(this.filterAfterBounceRight, 1000);
    rowSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;
    originalItemList = [];
    removedItemList = [];
    addedItemList = [];
    selectedRows = [];
    selectedRightRows = [];
    selectedInFilter = [];
    deselectedInFilter = [];
    deselectedInRightFilter = [];
    selectedInRightFilter = [];

    leftListColumns: Array<GenericColDef> = new Array<GenericColDef>();
    rightListColumns: Array<GenericColDef> = new Array<GenericColDef>();

    leftListGridContext = new GridContext({});
    rightListGridContext = new GridContext({});

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private commonService: CommonService,
        private restService: RestService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.createModalTemplateDynamic();
    }

    createModalTemplateDynamic(): void {
        if (this.modalData) {
            if (this.isProjectContextForMembershipModal()) {
                this.modalTitle = 'Add to / Remove from Projects';
                this.modalAutomationId = 'add-to-remove-from-projects-modal';
                this.modalHelpIndex = HelpIndex.project_member_select_popup_dlg;
                this.filterPlaceholder = 'Filter by Name and Description ...';
                this.leftListTitle = 'Available Projects';
                this.rightListTitle = 'Assigned Projects';
                this.nameColumn = 'Project Name';
            }

            this.createColumnDefsDynamic();

            this.roleColumnName = this.modalData.isMembershipModalOpenByUser ? 'User Role' : 'Group Role';
        }
    }

    createColumnDefsDynamic(): void {
        this.leftListColumns.push(
            {
                colId: 'checkbox', automationId: this.modalAutomationId, headerAutomationId: 'available-select-all-checkbox',
                checkboxSelection: true, headerCheckboxSelection: true,
                minWidth: 36, maxWidth: 36, width: 36,
                suppressSizeToFit: true, suppressMovable: true,
                tooltipValueGetter: (params) => {
                    const msg = this.tooltipValueGetter(params);
                    return msg;
                }
            }
        );
        this.rightListColumns.push(
            {
                colId: 'checkbox', automationId: this.modalAutomationId, headerAutomationId: 'asigned-select-all-checkbox',
                checkboxSelection: true, headerCheckboxSelection: true,
                minWidth: 36, maxWidth: 36, width: 36,
                suppressSizeToFit: true, suppressMovable: true,
                tooltipValueGetter: (params) => {
                    const msg = this.tooltipValueGetter(params);
                    return msg;
                }
            }
        );

        if (this.isProjectContextForMembershipModal()) {
            this.leftListColumns.push(
                {
                    headerName: 'Project Name', field: 'name', sortBy: 'name', automationId: this.modalAutomationId, cellTemplateType: CellTemplateType.nameIconLabel,
                },
                {
                    headerName: 'Project Description', field: 'description', automationId: this.modalAutomationId, cellTemplateType: CellTemplateType.basicLabel
                }
            );
            this.rightListColumns.push(
                {
                    headerName: 'Project Name', field: 'name', automationId: this.modalAutomationId, cellTemplateType: CellTemplateType.nameIconLabel,
                },
                {
                    headerName: 'Project Description', field: 'description', automationId: this.modalAutomationId, cellTemplateType: CellTemplateType.basicLabel,
                },
            );

        } else {
            this.leftListColumns.push(
                {
                    headerName: 'Organization Name', field: 'name', sortBy: 'name', automationId: this.modalAutomationId, cellTemplateType: CellTemplateType.nameIconLabel,
                }
            );
            this.rightListColumns.push(
                {
                    headerName: 'Organization Name', field: this.modalData.isMembershipModalOpenByUser ? 'organizationHandle' : 'name', automationId: this.modalAutomationId, cellTemplateType: CellTemplateType.nameIconLabel,
                },
            );
        }

        this.rightListColumns.push(
            {
                headerName: 'User Role', field: 'userRole', automationId: this.modalAutomationId, cellTemplateType: CellTemplateType.roleDropdownByProjOrOrg,
            }
        );
    }

    ngAfterViewInit(): void {
        this.loadData();
    }

    async loadData(): Promise<void> {
        if (this.modalData && this.leftRightSelectionBoxComponent?.genericGrids) {
            if (this.isProjectContextForMembershipModal()) {
                this.startBusySpinners(UsersAndGroupsMembershipActionType.addToRemoveFromProjects);

                const responseListRoles = await this.getRoles(this.modalData.isMembershipModalOpenByUser ? this.modalData.userName.customerName : this.modalData.group.orgName);
                this.roleList = responseListRoles?.roles.map(role => new Role(role));

                const requestProjectsData = this.createRequestForProjectsData();

                this.restService.post(this.modalData.isMembershipModalOpenByUser ? Endpoint.orgManager_listProjectsByUser : Endpoint.orgManager_listProjectsByGroup, requestProjectsData).then(response => {
                    if (response) {
                        // Available Projects
                        this.itemsLeft.length = 0;
                        for (let projectDetails of response?.availableProjectIdNameDescriptions) {
                            this.itemsLeft.push({
                                'projectId': projectDetails?.handle,
                                'name': projectDetails?.name,
                                'description': projectDetails?.description,
                                'iconClass': 'fa-book',
                                'iconColor': 'color-blue-01',
                            });
                        }

                        // Assigned Projects
                        if (Array.isArray(response?.projectRoles)) {
                            this.itemsRight.length = 0;
                            response?.projectRoles.forEach(assignedProj => {
                                this.itemsRight.push({
                                    'projectId': assignedProj?.key?.handle,
                                    'name': assignedProj?.key?.name,
                                    'description': assignedProj?.key?.description,
                                    'iconClass': 'fa-book',
                                    'iconColor': 'color-blue-01',
                                    'roleList': this.roleList.sort((a, b) => a?.name?.localeCompare(b?.name)),
                                    'selectedRole': this.roleList.find(r => r.name === assignedProj?.value?.name)
                                });
                            })
                        }
                        this.itemsLeft = this.itemsLeft.sort((a, b) => (a['name'] > b['name']) ? 1 : ((b['name'] > a['name']) ? -1 : 0));
                        this.itemsRight = this.itemsRight.sort((a, b) => (a['name'] > b['name']) ? 1 : ((b['name'] > a['name']) ? -1 : 0));
                        this.leftRightSelectionBoxComponent.setLeftItems(this.itemsLeft);
                        this.updateHeaderCheckboxState();
                        this.leftRightSelectionBoxComponent.setRightItems(this.itemsRight);
                        this.updateHeaderCheckboxState(false);
                        this.stopBusySpinners();
                        this.originalItemList = this.itemsRight.map(a => Object.assign({}, a));
                    }
                }, error => {
                    this.stopBusySpinners();
                });
            } else {
                this.startBusySpinners(UsersAndGroupsMembershipActionType.addToRemoveFromOrganizations);

                this.restService.post(Endpoint.realmManager_listOrganizations, { count: 0, listRoles: true, startIndex: 0, filters: [], systemScope: true }).then(async response => {
                    if (response && response.organizations) {
                        this.itemsLeft.length = 0;
                        response.organizations.forEach(org => {
                            org.organizationHandle = org.name;
                            org.iconClass = 'fa-bank';
                            org.iconColor = 'color-purple-03';
                            org.level = 0;
                            org.tooltip = org.deletePending ? 'This organization is pending deletion.' : '';
                            this.itemsLeft.push(org);
                        });
                        this.loadAssignedUsersOrGroups();
                    }
                }, error => {
                    this.stopBusySpinners();
                });
            }
        }
    }

    tooltipValueGetter(params): string {
        return (params && params.data.deletePending && this.modalData.isMembershipModalOpenByAction === UsersAndGroupsMembershipActionType.addToRemoveFromOrganizations ? 'This organization is pending deletion.' : '');
    }

    loadRoles(orgs): void {
        const orgName = this.modalData.isMembershipModalOpenByUser ? 'organizationHandle' : 'orgName';
        orgs = orgs.sort((a, b) => (a[orgName] > b[orgName]) ? 1 : ((b[orgName] > a[orgName]) ? -1 : 0));
        for (const org of orgs) {
            this.itemsLeft = this.itemsLeft.filter(e => {
                if (e.organizationHandle === org[orgName]) {
                    org.deletePending = e.deletePending;
                    org.roles = e.roles;
                }
                return e.organizationHandle !== org[orgName];
            });
            org.name = org[orgName];
            org.iconClass = 'fa-bank';
            org.iconColor = 'color-purple-03';
            org.level = 0;
            org.roleList = org.roles;
            org.selectedRole = this.getUserRole(org.roleHandles, org.roles);
            org.tooltip = org.deletePending ? 'This organization is pending deletion.' : '';
            org.roleList = org.roleList.sort((a, b) => a?.name?.localeCompare(b?.name));
        }
        this.leftRightSelectionBoxComponent.setRightItems(orgs);
        this.updateHeaderCheckboxState(false);
        this.leftRightSelectionBoxComponent.setLeftItems(this.itemsLeft);
        this.updateHeaderCheckboxState();
        this.stopBusySpinners();
        this.itemsRight = orgs;
        this.originalItemList = this.itemsRight.map(a => Object.assign({}, a));
    }

    getUserRole(roleHandles: Array<string>, orgRoles: any): any {
        if (roleHandles && roleHandles.length > 0) {
            return orgRoles.filter(r => r.handle === roleHandles[0]).pop();
        }
        return null;
    }

    filterDataLeft(str: string): void {
        this.debounceFilterLeft(str);
    }

    filterDataRight(str: string): void {
        this.debounceFilterRight(str);
    }

    filterAfterBounceLeft(queryStr: string): void {
        this.filterLeftQuery = queryStr;
        if (!queryStr || queryStr === '') {
            this.selectedInFilter = this.leftRightSelectionBoxComponent.genericGrids.first.gridApi.getSelectedRows()?.filter(row => !row.isDisabled).map(a => Object.assign({}, a));
            this.filteredLeftList = this.itemsLeft;
        } else {
            this.selectedRows = this.leftRightSelectionBoxComponent.genericGrids.first.gridApi.getSelectedRows()?.filter(row => !row.isDisabled).map(a => Object.assign({}, a));
            const query = queryStr.toLowerCase();
            if (this.isProjectContextForMembershipModal()) {
                this.filteredLeftList = _.filter(this.itemsLeft, (field) => {
                    return (field.name && field.name.toLowerCase().includes(query)) ||
                        (field.description && field.description.toLowerCase().includes(query));
                });
            } else {
                this.filteredLeftList = _.filter(this.itemsLeft, (field) => field.name ? field.name.toLowerCase().includes(query) : false);
            }
        }
        this.leftRightSelectionBoxComponent.setLeftItems(this.filteredLeftList);
        this.updateHeaderCheckboxState();

        this.leftRightSelectionBoxComponent.genericGrids.first.gridApi?.deselectAll();

        if (!queryStr || queryStr === '') {
            const filteredSelectedRows = this.selectedRows.filter(item => !this.deselectedInFilter?.some(deselected => deselected.name === item.name));

            filteredSelectedRows.forEach(item => {
                 const preSelField = this.leftRightSelectionBoxComponent.genericGrids.first.getData()?.find(field => field.name === item.name);

                if (preSelField) {
                    this.leftRightSelectionBoxComponent.genericGrids.first.findNode((node: RowNode) => node.data.name === item.name, true);
                }
            });
        } else {
            this.selectedRows.forEach(item => {
                const preSelField = this.leftRightSelectionBoxComponent.genericGrids.first.getData()?.find(field => field.name === item.name);
                    if (preSelField) {
                        this.leftRightSelectionBoxComponent.genericGrids.first.findNode((node: RowNode) => node.data.name === item.name, true);
                    }
            });
         }

        if (queryStr === '' && this.selectedInFilter.length > 0) {
            this.selectedInFilter.forEach(item => {
                const preSelField = this.leftRightSelectionBoxComponent.genericGrids.first.getData()?.find(field => field.name === item.name);
                if (preSelField) {
                    this.leftRightSelectionBoxComponent.genericGrids.first.findNode((node: RowNode) => node.data.name === item.name && !node.isSelected(), true);
                }
            });
        }
    }

    filterAfterBounceRight(queryStr: string): void {
        this.filterRightQuery = queryStr;
        if (!queryStr || queryStr === '') {
            this.selectedInRightFilter = this.leftRightSelectionBoxComponent.genericGrids.last.gridApi.getSelectedRows()?.filter(row => !row.isDisabled).map(a => Object.assign({}, a));
            this.filteredRightList = this.itemsRight;
        } else {
            this.selectedRightRows = this.leftRightSelectionBoxComponent.genericGrids.last.gridApi.getSelectedRows()?.filter(row => !row.isDisabled).map(a => Object.assign({}, a));
            const query = queryStr.toLowerCase();
            if (this.isProjectContextForMembershipModal()) {
                this.filteredRightList = _.filter(this.itemsRight, (field) => {
                    return (field.name && field.name.toLowerCase().includes(query)) ||
                        (field.description && field.description.toLowerCase().includes(query));
                });
            } else {
                this.filteredRightList = _.filter(this.itemsRight, (field) => field.name ? field.name.toLowerCase().includes(query) : false);
            }
        }
        this.leftRightSelectionBoxComponent.setRightItems(this.filteredRightList);
        this.updateHeaderCheckboxState(false);

        this.leftRightSelectionBoxComponent.genericGrids.last.gridApi?.deselectAll();

        if (!queryStr || queryStr === '') {
            const filteredSelectedRows = this.selectedRightRows.filter(item => !this.deselectedInRightFilter?.some(deselected => deselected.name === item.name));

            filteredSelectedRows.forEach(item => {
                 const preSelField = this.leftRightSelectionBoxComponent.genericGrids.last.getData()?.find(field => field.name === item.name);

                if (preSelField) {
                    this.leftRightSelectionBoxComponent.genericGrids.last.findNode((node: RowNode) => node.data.name === item.name, true);
                }
            });
        } else {
            this.selectedRightRows.forEach(item => {
                const preSelField = this.leftRightSelectionBoxComponent.genericGrids.last.getData()?.find(field => field.name === item.name);
                    if (preSelField) {
                        this.leftRightSelectionBoxComponent.genericGrids.last.findNode((node: RowNode) => node.data.name === item.name, true);
                    }
            });
         }

        if (queryStr === '' && this.selectedInRightFilter.length > 0) {
            this.selectedInRightFilter.forEach(item => {
                const preSelField = this.leftRightSelectionBoxComponent.genericGrids.last.getData()?.find(field => field.name === item.name);
                if (preSelField) {
                    this.leftRightSelectionBoxComponent.genericGrids.last.findNode((node: RowNode) => node.data.name === item.name && !node.isSelected(), true);
                }
            });
        }
    }

    loadAssignedUsersOrGroups = (): void => {
        const endpoint = this.modalData.isMembershipModalOpenByUser ? Endpoint.realmManager_listImportedCustomerUsersByUserName : Endpoint.realmManager_listImportedGroupsByGroupName;
        const data = this.modalData.isMembershipModalOpenByUser
            ? { count: 0, startIndex: 1, filters: [], systemScope: true, userName: this.modalData.userName.name }
            : { count: 0, startIndex: 1, filters: [], systemScope: true, groupName: this.modalData.group.name };

        this.restService.post(endpoint, data).then(response => {
            const responseArray = this.modalData.isMembershipModalOpenByUser ? response.users : response.groups;
            if (responseArray) {
                this.leftRightSelectionBoxComponent.genericGrids.last.isBusy = true;
                this.leftRightSelectionBoxComponent.genericGrids.last.rowHeight = 50;
                this.leftRightSelectionBoxComponent.genericGrids.last.isBusyMessage = 'Loading Assigned Organizations ...';
                this.loadRoles(responseArray);
            }
        });
    }

    okEnabled(): boolean {
        const isDataChanged = this.setItemsForAddOrRemoveOperations();
        return isDataChanged?.add?.length > 0 || isDataChanged?.remove?.length > 0 || isDataChanged?.update?.length > 0;
    }

    ok(): void {
        this.isBusy = this.isSaveDisabled = true;
        const projIdsOrOrgNames = [];
        const roleHandles = [];
        const result = this.setItemsForAddOrRemoveOperations();
        if (this.modalData.group) {
            if (this.modalData.isMembershipModalOpenByAction === UsersAndGroupsMembershipActionType.addToRemoveFromProjects) {
                this.isBusyMessage = 'Assigning Projects ...';
                if (result) {
                    result?.assignedProjects.forEach((proj) => {
                        projIdsOrOrgNames.push(proj?.projectId);
                        roleHandles.push(proj?.selectedRole?.handle);
                    });

                    const projIdRoleHandles = createOrgNameOrIdRoleHandles(projIdsOrOrgNames, roleHandles);

                    this.restService.post(Endpoint.orgManager_assignGroupToProjects, {
                        userOrGroupName: this.modalData?.group?.name,
                        projectIdRoles: projIdRoleHandles,
                        organizationName: this.modalData?.group?.orgName,
                    }).then((result) => {
                        this.isBusy = this.isSaveDisabled = false;
                        this.closeModal(true, this.setItemsForAddOrRemoveOperations());
                    }).catch(error => {
                        this.isBusy = this.isSaveDisabled = false;
                        this.closeModal(true, this.setItemsForAddOrRemoveOperations());
                    });
                }
            } else {
                this.isBusyMessage = 'Assigning Organizations ...';
                const promiseList = [];
                if (result && result?.add?.length > 0) {
                    result?.add.forEach((org) => {
                        projIdsOrOrgNames.push(org?.name);
                        roleHandles.push(org?.selectedRole?.handle);
                    });

                    const orgNameRoleHandles = createOrgNameOrIdRoleHandles(projIdsOrOrgNames, roleHandles);

                    promiseList.push(this.restService.post(Endpoint.adminOrgManager_addSystemGroupToOrg, {
                        systemObjectName: this.modalData?.group?.name,
                        orgNameRoleHandles: orgNameRoleHandles,
                    }));
                }
                if (result && result?.remove?.length > 0) {
                    const groupsToDelete = [];
                    result?.remove.forEach((org) => {
                        groupsToDelete.push(org.handle);
                    });

                    promiseList.push(this.restService.post(Endpoint.orgManager_deleteGroup, { taskDescription: 'Deleting System Groups ' + groupsToDelete.toString(), groupHandles: groupsToDelete }));
                }
                if (result && result?.update?.length > 0) {

                    const orgRoleHandles = [];
                    result?.update?.forEach((org) => {
                        orgRoleHandles.push( {name: this.modalData?.group?.name + '@' + org.orgName, roleHandles: [org.selectedRole?.handle] })
                    });
                    promiseList.push(this.restService.post(Endpoint
                        .adminOrgManager_updateSystemGroupRoles, {
                        groupsWithRoleHandles : orgRoleHandles,
                        systemScope: true,
                    }));
                }

                Promise.allSettled(promiseList).then(() => {
                    this.isBusy = this.isSaveDisabled = false;
                    this.closeModal(true, this.setItemsForAddOrRemoveOperations());
                });
            }
        } else if (this.modalData.userName) {
            if (this.modalData.isMembershipModalOpenByAction === UsersAndGroupsMembershipActionType.addToRemoveFromProjects) {
                this.isBusyMessage = 'Assigning Projects ...';
                if (result) {
                    result?.assignedProjects.forEach((proj) => {
                        projIdsOrOrgNames.push(proj?.projectId);
                        roleHandles.push(proj?.selectedRole?.handle);
                    });

                    const projIdRoleHandles = createOrgNameOrIdRoleHandles(projIdsOrOrgNames, roleHandles);

                    this.restService.post(Endpoint.userManager_assignUserToProjects, {
                        userOrGroupName: this.modalData?.userName?.userName,
                        projectIdRoles: projIdRoleHandles,
                        organizationName: this.modalData?.userName?.customerName
                    }).then((result) => {
                        this.isBusy = this.isSaveDisabled = false;
                        this.closeModal(true, this.setItemsForAddOrRemoveOperations());
                    }).catch(error => {
                        this.isBusy = this.isSaveDisabled = false;
                        this.closeModal(true, this.setItemsForAddOrRemoveOperations());
                    });
                }
            } else {
                this.isBusyMessage = 'Assigning Organizations ...';
                const promiseList = [];
                if (result && result?.add?.length > 0) {
                    result?.add.forEach((org) => {
                        projIdsOrOrgNames.push(org?.name);
                        roleHandles.push(org?.selectedRole?.handle);
                    });

                    const orgNameRoleHandles = createOrgNameOrIdRoleHandles(projIdsOrOrgNames, roleHandles);

                    promiseList.push(this.restService.post(Endpoint.adminOrgManager_addSystemUserToOrg, {
                        systemObjectName: this.modalData?.userName?.userName,
                        orgNameRoleHandles: orgNameRoleHandles,
                        orgName: this.modalData?.userName?.customerName
                    }));
                }
                if (result && result?.remove?.length > 0) {
                    result?.remove?.forEach((org) => {
                        projIdsOrOrgNames.push(org?.name);
                    });
                    promiseList.push(this.restService.post(Endpoint.adminOrgManager_removeSystemUsersFromOrganization, {
                        organizationNames : projIdsOrOrgNames,
                        userName: this.modalData?.userName.userName,
                        systemScope: true,
                    }));
                }
                if (result && result?.update?.length > 0) {
                    const orgRoleHandles = [];
                    result?.update?.forEach((org) => {
                        orgRoleHandles.push( {name: this.modalData?.userName.userName + '@' + org.organizationHandle, roleHandles: [org.selectedRole?.handle] })
                    });
                    promiseList.push(this.restService.post(Endpoint
                        .adminOrgManager_updateSystemUsersRoles, {
                        usersWithRoleHandles : orgRoleHandles,
                        systemScope: true,
                    }));
                }

                Promise.all(promiseList).then(() => {
                    this.isBusy = this.isSaveDisabled = false;
                    this.closeModal(true, this.setItemsForAddOrRemoveOperations());
                });
            }
        }
    }

    setItemsForAddOrRemoveOperations(): any {
        const add = [];
        const update = [];
        const remove = [];

        if (JSON.stringify(this.originalItemList) === JSON.stringify(this.itemsRight)) {
            return [];
        } else {
            this.itemsRight.forEach((item, index) => {
                const isItemPresentInOriginalList = this.originalItemList.find((i) => this.isProjectContextForMembershipModal() ? i?.projectId === item?.projectId : i?.handle === item?.handle);
                const isItemRoleDifferent = this.originalItemList.find((i) => this.isProjectContextForMembershipModal() ? i?.projectId === item?.projectId && i?.selectedRole?.handle !== item?.selectedRole?.handle : i?.handle === item?.handle && i?.selectedRole?.handle !== item?.selectedRole?.handle);
                if (!isItemPresentInOriginalList) {
                    add.push(item);
                }
                if (isItemRoleDifferent) {
                    update.push(item);
                }
            });
            this.originalItemList.forEach((item, index) => {
                const isItemPresentInOriginalList = this.itemsRight.find((i) => this.isProjectContextForMembershipModal() ? i?.projectId === item?.projectId : i?.handle === item?.handle);
                if (!isItemPresentInOriginalList) {
                    remove.push(item);
                }
            });
        }
        return { 'add': add, 'remove': remove, 'update': update, 'assignedProjects': this.itemsRight };
    }

    async copyOrRemoveItems(data: any): Promise<void> {
        if (data && data.mode && data.items) {
            data.items = this.filterLeftQuery !== '' && data.mode === 'add' ? this.addedItemList : data.items;
            this.leftRightSelectionBoxComponent.genericGrids.last.isBusy = true;
            const orgOrProjName = this.isProjectContextForMembershipModal() ? 'name' : this.modalData.isMembershipModalOpenByUser ? 'organizationHandle' : 'name';

            if (this.isProjectContextForMembershipModal()) {
                if (data.mode === 'add') {
                    const responseListRoles = await this.getRoles(this.modalData.isMembershipModalOpenByUser ? this.modalData.userName.customerName : this.modalData.group.orgName);
                    this.roleList = responseListRoles?.roles.map(role => new Role(role));
                    this.itemsRight.forEach(item => {
                        item.roleList = this.roleList;
                        item.roleList = item.roleList.sort((a, b) => a?.name?.localeCompare(b?.name));
                    })
                }
            }

            for (const item of data.items) {
                if (this.isProjectContextForMembershipModal()) {
                    if (data.mode === 'add') {
                        item.iconClass = 'fa-book';
                        item.iconColor = 'color-blue-01';
                        item.roleList = this.roleList;
                        item.roleList = item.roleList.sort((a, b) => a?.name?.localeCompare(b?.name));
                        item.selectedRole = item.selectedRole ? item.selectedRole : this.roleList.find(r => r.name === 'Organization Administrator');

                        this.addRemoveItems(data, item);
                    } else {
                        this.addRemoveItems(data, item);
                    }
                } else {
                    if (data.mode === 'add') {
                        await this.restService.post(Endpoint.adminOrgManager_listRoles, { objectType: 'ORGANIZATION', organizationName: item[orgOrProjName] }).then(responseListRoles => {
                            if (responseListRoles && responseListRoles.roles) {
                                this.roleList = responseListRoles.roles.map(role => new Role(role));
                            }

                            item.iconClass = 'fa-bank';
                            item.iconColor = 'color-purple-03';
                            item.level = 0;
                            item.roleList = item.roles;
                            item.roleList = item.roleList.sort((a, b) => a?.name?.localeCompare(b?.name));
                            item.selectedRole = item.selectedRole ?? (isNullOrUndefined(this.getUserRole(item.roleHandles, item.roles)) ? this.roleList.find(r => r.name === 'Organization Administrator') : this.getUserRole(item.roleHandles, item.roles));
                            this.addRemoveItems(data, item);
                        });
                    } else {
                        this.addRemoveItems(data, item);
                    }
                }
            }

            this.itemsLeft = this.itemsLeft.sort((a, b) => (a[orgOrProjName] > b[orgOrProjName]) ? 1 : ((b[orgOrProjName] > a[orgOrProjName]) ? -1 : 0));
            this.itemsRight = this.itemsRight.sort((a, b) => (a[orgOrProjName] > b[orgOrProjName]) ? 1 : ((b[orgOrProjName] > a[orgOrProjName]) ? -1 : 0));
            this.leftRightSelectionBoxComponent.setLeftItems(this.itemsLeft);
            this.updateHeaderCheckboxState();
            this.leftRightSelectionBoxComponent.setRightItems(this.itemsRight);
            this.updateHeaderCheckboxState(false);

            if (data.mode === 'add') {
                this.itemsRight.forEach((row, index, object) => {
                    const rowNode = this.isProjectContextForMembershipModal() ?
                        this.leftRightSelectionBoxComponent.genericGrids.last.gridApi.getRowNode(this.itemsRight.map(object => object?.projectId).indexOf(row?.projectId).toString()) :
                        this.leftRightSelectionBoxComponent.genericGrids.last.gridApi.getRowNode(this.itemsRight.map(object => object?.handle).indexOf(row?.handle).toString());
                    const isItemAdded = this.isProjectContextForMembershipModal() ?
                        this.addedItemList.find((i) => i?.projectId === row?.projectId) :
                        this.addedItemList.find((i) => i?.handle === row?.handle);
                    if (rowNode && isItemAdded) {
                        rowNode.setSelected(true);
                    }
                });
            }
            if (data.mode === 'remove') {
                this.removedItemList.slice().reverse().forEach((row, index, object) => {
                    const rowNode = this.isProjectContextForMembershipModal() ?
                        this.leftRightSelectionBoxComponent.genericGrids.first.gridApi.getRowNode(this.itemsLeft.map(object => object?.projectId).indexOf(row?.projectId).toString()) :
                        this.leftRightSelectionBoxComponent.genericGrids.first.gridApi.getRowNode(this.itemsLeft.map(object => object?.handle).indexOf(row?.handle).toString());
                    const isItemPresentInAvailableList = this.isProjectContextForMembershipModal() ?
                        this.itemsLeft.find((i) => i?.projectId === row?.projectId) :
                        this.itemsLeft.find((i) => i?.handle === row?.handle);
                    if (isItemPresentInAvailableList) {
                        rowNode.setSelected(true);
                        this.removedItemList.splice(row.length - 1 - index, 1);
                    }
                });

                const addedAndUnchecked = this.addedItemList.filter(({ handle: id1 }) => !this.removedItemList.some(({ handle: id2 }) => id2 === id1));
                addedAndUnchecked.slice().reverse().forEach((row, index, object) => {
                    this.addedItemList.splice(addedAndUnchecked.indexOf(row), 1).pop();
                });
            }
            this.leftRightSelectionBoxComponent.genericGrids.last.isBusy = false;
            if (this.leftRightSelectionBoxComponent.genericGrids && this.leftRightSelectionBoxComponent.genericGrids.first && this.leftRightSelectionBoxComponent.genericGrids.first.allRowsSelected) {
                this.leftRightSelectionBoxComponent.genericGrids.first.gridApi.deselectAll();
            }

            if (this.filterLeftQuery) {
                this.filterAfterBounceLeft(this.filterLeftQuery);
            }
            if (this.filterRightQuery) {
                this.filterAfterBounceRight(this.filterRightQuery);
            }
        }
    }

    updateHeaderCheckboxState(leftSide = true): void {
        setTimeout(() => {
            let grid;
            grid = leftSide ? this.leftRightSelectionBoxComponent.genericGrids.first : this.leftRightSelectionBoxComponent.genericGrids.last;
            const disabledClass = ' ag-select-all-disabled';

            const displayedRows: any[] = [];

            grid.gridApi.forEachNodeAfterFilter((node: any) => {
                displayedRows.push(node);
            });
            const noSelectableRows = displayedRows.length === 0 ||
                displayedRows.every(node => grid.gridApi.getRowNode(node.id)?.data.isDisabled);
            if (noSelectableRows && !grid.customGridClass.includes(disabledClass)) {
                grid.customGridClass += disabledClass;
            } else if (!noSelectableRows && grid.customGridClass.includes(disabledClass)) {
                grid.customGridClass = grid.customGridClass.replace(disabledClass, '');
            }

        });
    }

    private addRemoveItems(data: any, item: any) {
        const sourceArray = data.mode === 'add' ? this.itemsLeft : this.itemsRight;
        const targetArray = data.mode === 'add' ? this.itemsRight : this.itemsLeft;
        if (item) {
            if (data.mode === 'add') {
                const newItemIdx = sourceArray.indexOf(item);
                const orgNameOrProjId = this.isProjectContextForMembershipModal() ? 'projectId' : this.modalData.isMembershipModalOpenByUser ? 'organizationHandle' : 'name';

                const isItemAddedInList = this.isProjectContextForMembershipModal() ?
                    this.itemsRight.filter((i) => i?.projectId === item?.projectId) :
                    this.itemsRight.filter((i) => i?.handle === item?.handle);

                if (isItemAddedInList.length === 0) {
                    this.addedItemList.push(item);
                }

                if (newItemIdx > -1 && targetArray.findIndex(e => e[orgNameOrProjId] === sourceArray[newItemIdx][orgNameOrProjId]) === -1) {
                    targetArray.push(sourceArray[newItemIdx]);
                    sourceArray.splice(sourceArray.indexOf(item), 1).pop();
                }
                if (sourceArray[newItemIdx] && targetArray.findIndex(e => e[orgNameOrProjId] === sourceArray[newItemIdx][orgNameOrProjId]) !== -1) {
                    sourceArray.splice(sourceArray.indexOf(item), 1).pop();
                }

                const isItemAddedInDeletedInList = this.isProjectContextForMembershipModal() ?
                    this.removedItemList.filter((i) => i?.projectId === item?.projectId) :
                    this.removedItemList.filter((i) => i?.handle === item?.handle);
                if (isItemAddedInDeletedInList && isItemAddedInDeletedInList.length > 0) {
                    this.removedItemList.splice(this.removedItemList.indexOf(item), 1).pop();
                }
            } else {
                sourceArray.splice(sourceArray.indexOf(item), 1).pop();
                targetArray.push(item);
                this.leftRightSelectionBoxComponent.sortList(targetArray);
                this.removedItemList.push(item);
            }
        }
        if (data.mode === 'add' && this.leftRightSelectionBoxComponent.sortItemsRight) {
            this.leftRightSelectionBoxComponent.sortList(targetArray);
        }
    }

    rightListOnSelectionChanged = (event?: any) => {
        if (event.type === 'left' && this.filterLeftQuery !== '' && event.event.source === 'checkboxSelected') {
            this.deselectedInFilter = this.filteredLeftList;
        }
        if (event.type === 'right' && this.filterRightQuery !== '' && event.event.source === 'checkboxSelected') {
            this.deselectedInRightFilter = this.filteredRightList;
        }
        if (event?.items?.length !== 0 || Object?.keys((<any>this.leftRightSelectionBoxComponent?.genericGrids?.last?.gridApi)?.selectionController?.lastSelectedNode ?? {}).length > 0) {
                this.addedItemList = this.filterLeftQuery === '' ? event?.items : [...this.addedItemList, ...(event?.items?.filter(item => !this.addedItemList.some(existing => existing.name === item.name)) || [])];
        }
    }

    createRequestForProjectsData(): object {
        const request = { systemScope: false };
        if (this.modalData) {
            if (this.modalData.isMembershipModalOpenByUser) {
                Object.assign(request, { userName: this.modalData.userName.name });
                Object.assign(request, { organizationName: this.modalData.userName.customerName });
            } else {
                Object.assign(request, { groupName: this.modalData.group.name });
                Object.assign(request, { organizationName: this.modalData.group.orgName });
            }
        }
        return request;
    }

    startBusySpinners(action: UsersAndGroupsMembershipActionType): void {
        this.leftRightSelectionBoxComponent.genericGrids.first.isBusyMessage = action === UsersAndGroupsMembershipActionType.addToRemoveFromProjects ? 'Loading Available Projects ...' : 'Loading Available Organizations ...';
        this.leftRightSelectionBoxComponent.genericGrids.last.isBusyMessage = action === UsersAndGroupsMembershipActionType.addToRemoveFromProjects ? 'Loading Assigned Projects ...' : 'Loading Assigned Organizations ...';
        this.leftRightSelectionBoxComponent.genericGrids.last.isBusy = true;
        this.leftRightSelectionBoxComponent.genericGrids.first.isBusy = true;
        this.changeDetectorRef.detectChanges();
    }

    stopBusySpinners(): void {
        this.leftRightSelectionBoxComponent.genericGrids.first.isBusy = false;
        this.leftRightSelectionBoxComponent.genericGrids.last.isBusy = false;
    }

    getRoles(organizationName: string): any {
        return this.restService.post(Endpoint.adminOrgManager_listRoles, {
            objectType: 'ORGANIZATION',
            organizationName: organizationName
        });
    }

    isProjectContextForMembershipModal(): boolean {
        return this.modalData.isMembershipModalOpenByAction === UsersAndGroupsMembershipActionType.addToRemoveFromProjects ? true : false;
    }
}
